import { Link } from "react-router-dom";
import {
  AiOutlineHome,
  AiOutlineUser,
  AiOutlineBarChart,
  AiOutlineShopping,
  AiOutlineDollarCircle,
  AiOutlineBook,
  AiOutlineBank,
  AiOutlineTeam,
  AiOutlineRise,
  AiOutlineShop,
  AiOutlineGlobal,
  AiOutlineQrcode,
  AiOutlineSetting,
  AiOutlineHistory,
} from "react-icons/ai";
import { IoIosNotificationsOutline } from "react-icons/io";
import {
  GlassWater,
  UtensilsCrossed,
  IceCream,
  Sandwich,
  Coffee,
  Ham,
  Pizza,
  Fish,
  Layers,
  Star,
  Tag,
  Wine,
  Settings,
  Armchair,
  Users,
  ArrowLeftRight,
  ChevronRight,
} from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent } from "@/components/ui/card";
import hasPermission from "@/hooks/hasPermission";

const SECTION_ICON_MAP = {
  Overview: <AiOutlineHome className="text-xl" />,
  Customer: <Users size={20} />,
  "Till To Till Share": <ArrowLeftRight size={20} />,
  Notifications: <IoIosNotificationsOutline size={22} />,
  "Cost Management": <AiOutlineDollarCircle className="text-xl" />,
  "Menu Management": <AiOutlineBook className="text-xl" />,
  "Staff Management": <AiOutlineTeam className="text-xl" />,
  Settings: <AiOutlineSetting className="text-xl" />,
};

function QuickAccessCard({ section }) {
  const allowedChildren = (section.children || []).filter(
    (child) => !child.permission || hasPermission(child.permission)
  );
  const hasChildren = allowedChildren.length > 0;
  const icon = SECTION_ICON_MAP[section.title] || <AiOutlineHome className="text-xl" />;

  if (section.children && section.children.length > 0 && !hasChildren && !section.path) {
    return null;
  }

  return (
    <Card className="rounded-md border border-cblack6 bg-cblack3 shadow-none transition-colors hover:border-cblack7">
      <CardContent className="p-4">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cblack7 text-cyellow">
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-[15px] font-semibold leading-5 text-cwhite">
              {section.title}
            </p>
          </div>
        </div>

        {/* Children links */}
        {hasChildren && (
          <div className="space-y-1.5 pl-2">
            {allowedChildren.map((child) => (
              <Link
                key={child.title}
                to={child.path}
                className="flex items-center gap-2 text-[13px] text-cwhite3 hover:text-cyellow transition-colors group py-0.5"
              >
                <ChevronRight size={14} className="text-cblack6 group-hover:text-cyellow shrink-0" />
                <span className="truncate">{child.title}</span>
              </Link>
            ))}
          </div>
        )}

    
        {!hasChildren && section.path && (
          <Link
            to={section.path}
            className="inline-flex items-center gap-1.5 text-[13px] text-cyellow hover:text-cyellow/80 transition-colors mt-1"
          >
            <span>Go to {section.title}</span>
            <ChevronRight size={14} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function Overview() {

  const sections = backofficeLinks
    .filter((link) => link.title !== "Overview")
    .filter((link) => !link.permission || hasPermission(link.permission));

  return (
    <div className="min-h-screen w-full bg-color1">
      <Layout2
        LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
        pageName={{ title: "Back Office Overview" }}
        paths={[
          { name: "Home", path: "/home" },
          { name: "Back Office", path: "/backoffice/overview" },
          { name: "Overview" },
        ]}
      >
        <div className="container mx-auto space-y-6 px-4 pb-8 pt-4 text-cwhite">
          {/* Section title */}
          <div className="flex flex-col items-center text-center">
            <h2 className="text-[16px] font-semibold leading-5 text-cyellow">
              Back Office Quick Access
            </h2>
            <p className="mt-1 text-[12px] text-cwhite3">
              Navigate to any back office section from here
            </p>
          </div>

          {/* Quick access grid */}
          <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            {sections?.map((section) => (
              <QuickAccessCard key={section.title} section={section} />
            ))}
          </div>
        </div>
      </Layout2>
    </div>
  );
}

export default Overview;
